import React from 'react';
import useSecurityDashboard from './useSecurityDashboard';
import SecurityPageShell from './components/SecurityPageShell';
import SecurityMetricsGrid from './components/SecurityMetricsGrid';
import SecurityAlertsPanel from './components/SecurityAlertsPanel';
import SecurityProtectionPanel from './components/SecurityProtectionPanel';
import SecurityGeoThreatsPanel from './components/SecurityGeoThreatsPanel';
import SecurityIncidentsPanel from './components/SecurityIncidentsPanel';
import SecuritySummaryPanel from './components/SecuritySummaryPanel';
import SecurityThreatAnalysisPanel from './components/SecurityThreatAnalysisPanel';
import SecurityOverviewGrid from './components/SecurityOverviewGrid';
import SecurityBlockedIPsPanel from './components/SecurityBlockedIPsPanel';
import SecurityDiagnosticsPanel from './components/SecurityDiagnosticsPanel';
import SecurityEventsTable from './components/SecurityEventsTable';
import SecurityUnblockConfirm from './components/SecurityUnblockConfirm';

export default function SecurityDashboard() {
  const {
    dashboardData,
    blockedIPsItems,
    blockedIPsPagination,
    blockedIPsLoading,
    fetchBlockedIPs,
    blockedIPsRefreshKey,
    healthData,
    diagnosticsData,
    diagnosticsLoading,
    lastUpdatedAt,
    protectionStats,
    loading,
    refreshing,
    error,
    isAuthenticated,
    actionMessage,
    setActionMessage,
    eventSeverityFilter,
    setEventSeverityFilter,
    filteredRecentEvents,
    eventsSectionRef,
    unblockConfirmIp,
    unblockBusy,
    fetchDashboardData,
    requestUnblockIP,
    cancelUnblockIP,
    confirmUnblockIP,
    reviewCriticalEvents,
    scrollToThreatAnalysis,
  } = useSecurityDashboard();

  if (!isAuthenticated) {
    return (
      <div className="vx-sec-page">
        <div className="vx-sec-state vx-sec-state--error">
          <h2>Access denied</h2>
          <p>{error || 'Admin privileges are required to view the security dashboard.'}</p>
        </div>
      </div>
    );
  }

  if (loading && !dashboardData) {
    return (
      <div className="vx-sec-page">
        <div className="vx-sec-state vx-sec-state--loading">Loading security dashboard…</div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="vx-sec-page">
        <div className="vx-sec-state vx-sec-state--error">
          <h2>Error</h2>
          <p>{error}</p>
          <button type="button" className="btn" onClick={fetchDashboardData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="vx-sec-page">
        <div className="vx-sec-state vx-sec-state--error">No security data available</div>
      </div>
    );
  }

  return (
    <div className="vx-sec-page">
      <SecurityPageShell
        refreshing={refreshing}
        error={error}
        actionMessage={actionMessage}
        onDismissAction={() => setActionMessage(null)}
        onRetry={fetchDashboardData}
      >
        <SecurityMetricsGrid dashboardData={dashboardData} />

        <SecurityAlertsPanel
          dashboardData={dashboardData}
          protectionStats={protectionStats}
          onReviewCritical={reviewCriticalEvents}
          onViewHighRisk={scrollToThreatAnalysis}
        />

        <SecurityProtectionPanel protectionStats={protectionStats} />

        <SecurityGeoThreatsPanel threatAnalysis={dashboardData.threatAnalysis} />

        <SecurityIncidentsPanel recentEvents={dashboardData.recentEvents} />

        <SecuritySummaryPanel dashboardData={dashboardData} />

        <SecurityThreatAnalysisPanel
          dashboardData={dashboardData}
          protectionStats={protectionStats}
        />

        <SecurityOverviewGrid dashboardData={dashboardData} />

        <SecurityBlockedIPsPanel
          blockedIPs={blockedIPsItems}
          pagination={blockedIPsPagination}
          loading={blockedIPsLoading}
          unblockBusy={unblockBusy}
          onUnblock={requestUnblockIP}
          onFetchBlockedIPs={fetchBlockedIPs}
          refreshKey={blockedIPsRefreshKey}
        />

        <SecurityDiagnosticsPanel
          healthData={healthData}
          diagnosticsData={diagnosticsData}
          lastUpdatedAt={lastUpdatedAt}
          loading={diagnosticsLoading}
        />

        <SecurityEventsTable
          sectionRef={eventsSectionRef}
          eventSeverityFilter={eventSeverityFilter}
          onClearFilter={() => setEventSeverityFilter('ALL')}
          filteredEvents={filteredRecentEvents}
        />
      </SecurityPageShell>

      <SecurityUnblockConfirm
        ipAddress={unblockConfirmIp}
        busy={unblockBusy}
        onCancel={cancelUnblockIP}
        onConfirm={confirmUnblockIP}
      />
    </div>
  );
}
